import type {
  Bbox,
  Category,
  Listing,
  ListingState,
  LocationPrecision,
  PriceUnit,
  ProduceStatus,
  Producer,
  Product,
} from '@/types/domain';
import { api, apiUpload, assetUrl, ApiError } from './apiClient';

export type ListedWithin = '1d' | '7d' | '30d';

export interface ListingFilters {
  categoryId?: string;
  productId?: string;
  query?: string;
  status?: Listing['status'];
  minPrice?: number;
  maxPrice?: number;
  listedWithin?: ListedWithin;
  city?: string;
  district?: string;
  neighbourhoodId?: number;
  producerId?: string;
}

/** Mixed autocomplete payload (endpoint: GET /listings/search?q=…). */
export interface SearchSuggestions {
  listings: Listing[];
  producers: Producer[];
  /** Every match, not the page — what "Daha fazla yükle" counts down from. */
  total: number;
}

/** One page of autocomplete rows; the server pages, the client accumulates. */
export const SEARCH_PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Wire mappers — live endpoints. Single seam between the real API's snake_case
// shape and the UI-shaped domain types; fixture functions further below
// stay fixture-backed until their own wiring chat.
// ---------------------------------------------------------------------------

const FALLBACK_COORDS = { lon: 32.49, lat: 37.87 }; // Turkey-center — used when a response has no usable geom

interface ApiCategory {
  id: number;
  name: string;
  icon: string;
  icon_key?: string | null;
}

const toCategory = (c: ApiCategory): Category => ({
  id: String(c.id),
  name: c.name,
  icon: c.icon,
  iconKey: c.icon_key ?? '',
});

interface ApiProduct {
  id: number;
  category_id: number;
  name: string;
  icon: string | null;
  icon_key?: string | null;
  is_other?: boolean;
}

const toProduct = (p: ApiProduct): Product => ({
  id: String(p.id),
  categoryId: String(p.category_id),
  name: p.name,
  icon: p.icon ?? '',
  iconKey: p.icon_key ?? '',
  isOther: p.is_other ?? false,
});

interface ApiEmbeddedProducer {
  id: number;
  name: string;
  avatar_url: string | null;
  city: string | null;
  badge_key: string | null;
  phone_public: boolean;
}

/**
 * badge_key has no computed values yet (the badge service isn't built — the
 * column is always null on live data today). Passed through raw rather than
 * mapped to a Turkish label: inventing a label dictionary for a naming scheme
 * that doesn't exist yet would just be a guess.
 */
export const toEmbeddedProducer = (p: ApiEmbeddedProducer): Producer => ({
  id: String(p.id),
  displayName: p.name,
  avatarUrl: p.avatar_url ?? undefined,
  city: p.city ?? '',
  district: '', // FIXTURE-GAP: not selected by get-listing-by-id
  trustBadge: p.badge_key ?? undefined,
  phonePublic: p.phone_public === true,
  isOnline: false, // FIXTURE-GAP: no presence system yet
  isFavorite: false, // FIXTURE-GAP: favorites module not wired
});

interface ApiListingBase {
  id: number;
  user_id: number;
  title: string;
  category_id: number;
  product_id: number | null;
  /** Joined by the detail endpoint; the list endpoints send the flat fields instead. */
  product?: { id: number; name: string; icon: string | null; icon_key?: string | null } | null;
  product_type: string | null;
  custom_product_name?: string | null;
  description: string | null;
  price: number | string;
  price_unit: PriceUnit;
  quantity: number | string | null;
  quantity_unit: PriceUnit | null;
  status: ProduceStatus;
  harvest_year: number | null;
  harvest_time: string | null;
  county_code?: number | null;
  city: string | null;
  town_id?: number | null;
  district: string | null;
  neighbourhood_id?: number | null;
  neighbourhood: string | null;
  location_precision: LocationPrecision;
  latitude?: number | null;
  longitude?: number | null;
  moderation_status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string | null;
  is_active: boolean;
  admin_removed: boolean;
  admin_remove_reason?: string | null;
  expires_at: string;
  created_at: string;
  first_approved_at?: string | null;
}

const isExpired = (l: ApiListingBase) => new Date(l.expires_at).getTime() <= Date.now();

/**
 * Picks the ONE label for the badge. Display concern only — the underlying
 * facts stay orthogonal on `isPaused` / `isExpired`.
 *
 * The badge always names THE THING THE SELLER IS BEING ASKED TO FIX NEXT, so
 * it matches the single action offered on the card. That is why `paused`
 * outranks `expired`: on a listing that is both, the offered action is
 * "Yayına Al", and a badge reading "Süresi Doldu" next to it would describe a
 * different problem than the button solves. Once resumed,
 * the badge flips to "Süresi Doldu" and the second step becomes the subject.
 */
const deriveState = (l: ApiListingBase): ListingState => {
  if (l.admin_removed) return 'removed';
  if (l.moderation_status === 'rejected') return 'rejected';
  if (l.moderation_status === 'pending') return 'pending';
  if (!l.is_active) return 'paused';
  if (isExpired(l)) return 'expired';
  return 'active';
};

export const toListing = (
  l: ApiListingBase,
  photoEntries: { id: string; key: string }[]
): Listing => ({
  id: String(l.id),
  title: l.title,
  categoryId: String(l.category_id),
  productId: l.product_id != null ? String(l.product_id) : '',
  productName: l.custom_product_name || (l.product?.name ?? ''),
  customProductName: l.custom_product_name ?? '',
  productIcon: l.product?.icon ?? '',
  productIconKey: l.product?.icon_key ?? '',
  productType: l.product_type ?? '',
  description: l.description ?? '',
  price: Number(l.price),
  priceUnit: l.price_unit,
  quantity: l.quantity != null ? Number(l.quantity) : 0,
  quantityUnit: l.quantity_unit ?? 'kg',
  status: l.status,
  harvestYear: l.harvest_year ?? new Date().getFullYear(),
  harvestRange: l.harvest_time ?? undefined,
  countyCode: l.county_code ?? undefined,
  city: l.city ?? '',
  townId: l.town_id ?? undefined,
  district: l.district ?? '',
  neighbourhoodId: l.neighbourhood_id ?? undefined,
  neighbourhood: l.neighbourhood ?? '',
  locationPrecision: l.location_precision,
  coords:
    l.latitude != null && l.longitude != null
      ? { lon: l.longitude, lat: l.latitude }
      : FALLBACK_COORDS, // FIXTURE-GAP: my-listings excludes geom — no pin needed on those cards
  photos: photoEntries.map(({ id, key }) => ({ id, url: assetUrl(key), alt: l.title })),
  producerId: String(l.user_id),
  state: deriveState(l),
  rejectionReason: l.rejection_reason ?? undefined,
  adminRemoved: l.admin_removed,
  adminRemoveReason: l.admin_remove_reason ?? undefined,
  expiresAt: l.expires_at,
  isPaused: !l.is_active,
  isExpired: isExpired(l),
  isFavorite: false, // FIXTURE-GAP: favorites module not wired
  favoriteCount: 0, // FIXTURE-GAP: favorites module not wired
  createdAt: l.created_at,
  listedAt: l.first_approved_at ?? undefined,
});

export async function getCategories(): Promise<Category[]> {
  const rows = await api<ApiCategory[]>('/categories/getCategories');
  return rows.map(toCategory);
}

/**
 * Every category's products in one call (~100 rows). The create form filters
 * them in memory, so tapping a category opens the dropdown with no request.
 */
export async function getProducts(): Promise<Product[]> {
  const rows = await api<ApiProduct[]>('/products/getProducts');
  return rows.map(toProduct);
}

export interface ListingWithDetails {
  listing: Listing;
  producer: Producer;
  category: Category;
}

interface ApiListingDetail extends ApiListingBase {
  photos: { id: number; object_key: string }[];
  producer: ApiEmbeddedProducer;
  category: ApiCategory;
}

export async function getListing(id: string): Promise<ListingWithDetails | undefined> {
  try {
    const { photos, producer, category, ...rest } = await api<ApiListingDetail>(
      `/listings/get-listing-by-id/${id}`
    );
    return {
      listing: toListing(
        rest,
        photos.map((p) => ({ id: String(p.id), key: p.object_key }))
      ),
      producer: toEmbeddedProducer(producer),
      category: toCategory(category),
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return undefined;
    throw err;
  }
}

/**
 * Owner's phone for "Hemen Ara" — fetched only on tap (never bundled into
 * getListing). 404 when the listing isn't publicly visible; the caller maps
 * ApiError.status to a Turkish message.
 */
export async function getListingContact(id: string): Promise<{ phone: string }> {
  return api<{ phone: string }>(`/listings/${id}/contact`);
}

interface ApiMyListing extends ApiListingBase {
  thumbnail: string | null;
  category: ApiCategory;
}

export async function getMyListings(): Promise<Listing[]> {
  const rows = await api<ApiMyListing[]>('/listings/my-listings');
  return rows.map(({ thumbnail, category, ...rest }) =>
    toListing(rest, thumbnail ? [{ id: 'thumb', key: thumbnail }] : [])
  );
}

interface ApiSearchResult {
  id: number;
  title: string;
  product_type: string | null;
  custom_product_name?: string | null;
  city: string | null;
  district: string | null;
  neighbourhood: string | null;
  latitude: number | null;
  longitude: number | null;
  location_precision: LocationPrecision;
  /** Cosmetic only — which emoji the autocomplete row and its map pin draw. */
  category_id: number;
  product_icon: string | null;
  product_icon_key?: string | null;
}

/**
 * The search endpoint returns anti-scrape thin fields only (no price, no
 * producer, no photos) — those are zeroed/empty here. searchSuggestions.tsx
 * hides the price row and photo when they're not real data.
 */
const toSearchListing = (r: ApiSearchResult): Listing => ({
  id: String(r.id),
  title: r.title,
  categoryId: String(r.category_id),
  productId: '', // FIXTURE-GAP: not in the thin search response
  productName: r.custom_product_name ?? '', // FIXTURE-GAP: the catalogue name is not in the thin search response
  customProductName: r.custom_product_name ?? '',
  // Empty when the product has none: useListingIcons then resolves the
  // category's own emoji from categoryId above.
  productIcon: r.product_icon ?? '',
  productIconKey: r.product_icon_key ?? '',
  productType: r.product_type ?? '',
  description: '',
  price: 0, // FIXTURE-GAP: not in the thin search response
  priceUnit: 'kg',
  quantity: 0,
  quantityUnit: 'kg',
  status: 'inCrate',
  harvestYear: new Date().getFullYear(),
  city: r.city ?? '',
  district: r.district ?? '',
  neighbourhood: r.neighbourhood ?? '',
  locationPrecision: r.location_precision,
  coords:
    r.latitude != null && r.longitude != null
      ? { lon: r.longitude, lat: r.latitude }
      : FALLBACK_COORDS,
  photos: [],
  producerId: '',
  state: 'active', // search only returns approved/active/unexpired rows (same filter as the map layer)
  isFavorite: false,
  favoriteCount: 0,
  createdAt: new Date().toISOString(),
});

/** Producer row in the autocomplete — the same masked public card the profile uses. */
interface ApiSearchProducer {
  id: number;
  name: string;
  avatar_url: string | null;
  city: string | null;
  badge_key: string | null;
}

const toSearchProducer = (p: ApiSearchProducer): Producer => ({
  id: String(p.id),
  displayName: p.name,
  avatarUrl: p.avatar_url ?? undefined,
  city: p.city ?? '',
  district: '', // the search row carries city only; the profile has the rest
  isOnline: false,
  isFavorite: false,
});

export async function getSearchSuggestions(query: string, offset = 0): Promise<SearchSuggestions> {
  const q = query.trim();
  if (q.length < 3) return { listings: [], producers: [], total: 0 }; // backend also no-ops below 3 chars — skip the call
  const page = await api<{
    items: ApiSearchResult[];
    total: number;
    producers?: ApiSearchProducer[];
  }>(`/listings/search?q=${encodeURIComponent(q)}&offset=${offset}`);
  return {
    listings: page.items.map(toSearchListing),
    // Absent on later pages: producers are unpaged and ride along with offset 0.
    producers: (page.producers ?? []).map(toSearchProducer),
    total: page.total,
  };
}

/** What the create-listing form submits (endpoint: POST /listings/create-listing). */
export interface CreateListingInput {
  title: string;
  categoryId: string;
  productId: string;
  /** The typed name for the "Listede yok" product; null for any other. */
  customProductName: string | null;
  productType: string;
  description: string;
  price: number;
  priceUnit: Listing['priceUnit'];
  quantity: number;
  quantityUnit: Listing['quantityUnit'];
  status: Listing['status'];
  harvestYear: number;
  harvestRange?: string;
  /**
   * The chosen address as ids only. The server looks the names up from
   * `neighbourhoodId`, so a client can never store a name that disagrees with
   * the id beside it.
   */
  countyCode: number;
  townId: number;
  neighbourhoodId: number;
  /**
   * Producer-placed pin, or null when they skipped it. Without one the server
   * places the listing inside the chosen neighbourhood and marks it approximate.
   */
  coords: { lat: number; lon: number } | null;
  /** Active-listing duration, backend-enforced 1/3/6 months. */
  durationMonths: 1 | 3 | 6;
}

export async function createListing(input: CreateListingInput): Promise<Listing> {
  const row = await api<ApiListingBase>('/listings/create-listing', {
    method: 'POST',
    body: {
      title: input.title,
      category_id: Number(input.categoryId),
      // Empty means "not picked", not product 0 — Number('') would send a value
      // the server can only reject.
      product_id: input.productId ? Number(input.productId) : undefined,
      product_type: input.productType || undefined,
      custom_product_name: input.customProductName,
      description: input.description || undefined,
      price: input.price,
      price_unit: input.priceUnit,
      quantity: input.quantity || undefined,
      quantity_unit: input.quantityUnit,
      status: input.status,
      harvest_year: input.harvestYear,
      harvest_time: input.harvestRange || undefined,
      county_code: input.countyCode,
      town_id: input.townId,
      neighbourhood_id: input.neighbourhoodId,
      // Sent as a pair or not at all — the server rejects half a pin.
      latitude: input.coords?.lat,
      longitude: input.coords?.lon,
      duration_months: input.durationMonths,
    },
  });
  // create-listing's INSERT ... RETURNING * has no photos/producer/category to
  // map, and the raw response returns PostGIS `geom`, not lat/lon — so reuse the
  // coords we just submitted. Photos are a separate call — see
  // uploadListingPhotos() — since the listing needs an id to attach them to.
  // Without a pin there is nothing local to reuse: the server placed the
  // listing at its neighbourhood's point, which this response does not carry.
  return { ...toListing(row, []), ...(input.coords ? { coords: input.coords } : {}) };
}

/** Why the server's automatic review refused a photo. */
export type PhotoRejection = 'nsfw' | 'notProduce';

const toRejection = (reason: string | null | undefined): PhotoRejection | null =>
  reason === 'nsfw' ? 'nsfw' : reason === 'not_produce' ? 'notProduce' : null;

export interface PhotoSuggestion {
  categoryId: string | null;
  productId: string | null;
  title: string | null;
}

export interface PhotoCheck {
  /** Refused: the photo must be dropped. Anything else keeps it. */
  rejection: PhotoRejection | null;
  /** Only when the review was confident: what the form may fill in. */
  suggestion: PhotoSuggestion | null;
}

interface ApiPhotoCheck {
  enabled: boolean;
  verdict?: 'accepted' | 'flagged' | 'rejected';
  reason?: string | null;
  suggestion?: { categoryId: number | null; productId: number | null; title: string | null } | null;
}

/**
 * POST /listings/analyze-photo — the server's review of one picked photo,
 * before any listing exists. Advisory for the form: the upload is reviewed
 * again and that review decides. A review that is switched off answers like a
 * photo with nothing to say.
 */
export async function analyzeListingPhoto(file: File): Promise<PhotoCheck> {
  const formData = new FormData();
  formData.append('photo', file);
  const res = await apiUpload<ApiPhotoCheck>('/listings/analyze-photo', formData);
  if (!res.enabled) return { rejection: null, suggestion: null };
  const s = res.suggestion;
  return {
    rejection: res.verdict === 'rejected' ? toRejection(res.reason) : null,
    suggestion: s
      ? {
          categoryId: s.categoryId != null ? String(s.categoryId) : null,
          productId: s.productId != null ? String(s.productId) : null,
          title: s.title,
        }
      : null,
  };
}

export interface UploadedPhotos {
  photos: Listing['photos'];
  /** One entry per photo the server's review refused; those were not stored. */
  rejected: PhotoRejection[];
}

/**
 * POST /listings/:id/photos, called after createListing() resolves (needs the
 * new listing's id). Owner-only, max 5 total, resized + EXIF-stripped
 * server-side. Returns the resulting photo URLs in sort order.
 */
export async function uploadListingPhotos(
  listingId: string,
  files: File[]
): Promise<UploadedPhotos> {
  if (files.length === 0) return { photos: [], rejected: [] };
  const formData = new FormData();
  files.forEach((file) => formData.append('photos', file));
  const { photos, rejected = [] } = await apiUpload<{
    photos: { id: number; object_key: string }[];
    rejected?: { index: number; reason: string }[];
  }>(`/listings/${listingId}/photos`, formData);
  return {
    photos: photos.map((p) => ({ id: String(p.id), url: assetUrl(p.object_key), alt: '' })),
    rejected: rejected.map((r) => toRejection(r.reason) ?? 'notProduce'),
  };
}

/** DELETE /listings/:id/photos/:photoId — owner-only. */
export async function deleteListingPhoto(listingId: string, photoId: string): Promise<void> {
  await api(`/listings/${listingId}/photos/${photoId}`, { method: 'DELETE' });
}

/** What the edit-listing form submits — all fields optional (PUT /listings/update-listing/:id). */
/** Permanent — the owner's own listing, never one an admin took down (403). */
export async function deleteListing(id: string): Promise<void> {
  await api(`/listings/delete-listing/${id}`, { method: 'DELETE' });
}

export interface UpdateListingInput {
  title?: string;
  categoryId?: string;
  productId?: string;
  /** null clears it, which leaving the "Listede yok" product requires. */
  customProductName?: string | null;
  productType?: string;
  description?: string;
  price?: number;
  priceUnit?: Listing['priceUnit'];
  quantity?: number;
  quantityUnit?: Listing['quantityUnit'];
  status?: Listing['status'];
  harvestYear?: number;
  harvestRange?: string;
  countyCode?: number;
  townId?: number;
  neighbourhoodId?: number;
  /**
   * A pin, or null for none. Sending a neighbourhood without a pin moves the
   * listing to a point inside that neighbourhood and marks it approximate.
   */
  coords?: { lat: number; lon: number } | null;
}

/**
 * Any edit returns the listing to 'pending' — enforced server-side,
 * not something the client controls. duration_months is deliberately not
 * part of this: renewal is a separate mechanism, not general editing.
 * Photos are managed separately (uploadListingPhotos/deleteListingPhoto).
 */
export async function updateListing(id: string, input: UpdateListingInput): Promise<Listing> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.categoryId !== undefined) body.category_id = Number(input.categoryId);
  if (input.productId) body.product_id = Number(input.productId);
  if (input.customProductName !== undefined) body.custom_product_name = input.customProductName;
  if (input.productType !== undefined) body.product_type = input.productType;
  if (input.description !== undefined) body.description = input.description;
  if (input.price !== undefined) body.price = input.price;
  if (input.priceUnit !== undefined) body.price_unit = input.priceUnit;
  if (input.quantity !== undefined) body.quantity = input.quantity;
  if (input.quantityUnit !== undefined) body.quantity_unit = input.quantityUnit;
  if (input.status !== undefined) body.status = input.status;
  if (input.harvestYear !== undefined) body.harvest_year = input.harvestYear;
  if (input.harvestRange !== undefined) body.harvest_time = input.harvestRange;
  if (input.countyCode !== undefined) body.county_code = input.countyCode;
  if (input.townId !== undefined) body.town_id = input.townId;
  if (input.neighbourhoodId !== undefined) body.neighbourhood_id = input.neighbourhoodId;
  // The pin is the producer's, not derived from the chosen address. Omitting it
  // is meaningful rather than missing: an edit that names a neighbourhood and
  // carries no pin re-places the listing at that neighbourhood's point.
  if (input.coords) {
    body.latitude = input.coords.lat;
    body.longitude = input.coords.lon;
  }

  const row = await api<ApiListingBase>(`/listings/update-listing/${id}`, {
    method: 'PUT',
    body,
  });
  return toListing(row, []); // photos unaffected by this call — fetch separately if needed
}

/** Seller-picked renewal windows — same set the create form offers. */
export const RENEWAL_DURATIONS = [1, 3, 6] as const;
export type RenewalDuration = (typeof RENEWAL_DURATIONS)[number];

/**
 * Re-publishes an expired listing (or extends a live one) for another
 * 1/3/6 months. Deliberately NOT an edit: the backend leaves
 * moderation_status untouched, so renewing never sends the listing back to
 * the approval queue.
 */
export async function renewListing(id: string, durationMonths: RenewalDuration): Promise<Listing> {
  const row = await api<ApiListingBase>(`/listings/${id}/renew`, {
    method: 'PUT',
    body: { duration_months: durationMonths },
  });
  return toListing(row, []); // photos unaffected — the card keeps the thumbnail it already has
}

/**
 * The seller's own switch: stop / resume taking calls and messages. Kept
 * separate from renewal on purpose — pausing does not touch the expiry clock,
 * and resuming does not un-expire anything.
 */
export async function pauseListing(id: string): Promise<Listing> {
  const row = await api<ApiListingBase>(`/listings/${id}/pause`, { method: 'PUT' });
  return toListing(row, []);
}

export async function unpauseListing(id: string): Promise<Listing> {
  const row = await api<ApiListingBase>(`/listings/${id}/pause`, { method: 'DELETE' });
  return toListing(row, []);
}

interface ApiPublicListingCard {
  id: number;
  category_id: number;
  product_id: number | null;
  product_name: string | null;
  product_icon: string | null;
  product_icon_key?: string | null;
  title: string;
  product_type: string | null;
  custom_product_name?: string | null;
  price: number;
  price_unit: PriceUnit;
  quantity: number | null;
  quantity_unit: PriceUnit | null;
  status: ProduceStatus;
  city: string | null;
  district: string | null;
  neighbourhood: string | null;
  location_precision: LocationPrecision;
  thumbnail: string | null;
  latitude: number;
  longitude: number;
}

/**
 * Full card data for Explore's map/list browsing and a producer's other
 * listings — approved+active+unexpired only (server-side filtered). Not the
 * anti-scrape thin map layer, which the map loads on its own. description/producer still only load
 * via getListing() when a specific listing is opened.
 */
const toPublicListingCard = (r: ApiPublicListingCard): Listing => ({
  id: String(r.id),
  title: r.title,
  categoryId: String(r.category_id),
  productId: r.product_id != null ? String(r.product_id) : '',
  productName: r.custom_product_name || (r.product_name ?? ''),
  customProductName: r.custom_product_name ?? '',
  productIcon: r.product_icon ?? '',
  productIconKey: r.product_icon_key ?? '',
  productType: r.product_type ?? '',
  description: '', // FIXTURE-GAP: full detail loads via getListing() on open
  price: r.price,
  priceUnit: r.price_unit,
  quantity: r.quantity ?? 0,
  quantityUnit: r.quantity_unit ?? 'kg',
  status: r.status,
  harvestYear: new Date().getFullYear(),
  city: r.city ?? '',
  district: r.district ?? '',
  neighbourhood: r.neighbourhood ?? '',
  locationPrecision: r.location_precision,
  coords: { lon: r.longitude, lat: r.latitude },
  photos: r.thumbnail ? [{ id: `${r.id}-0`, url: assetUrl(r.thumbnail), alt: r.title }] : [],
  producerId: '',
  state: 'active', // endpoint already filters to approved/active/unexpired
  isFavorite: false,
  favoriteCount: 0,
  createdAt: new Date().toISOString(),
});

export const publicListingsQuery = (
  filters: ListingFilters,
  offset: number,
  limit?: number
): string => {
  const params = new URLSearchParams();
  if (filters.categoryId) params.set('category_id', filters.categoryId);
  if (filters.productId) params.set('product_id', filters.productId);
  if (filters.status) params.set('status', filters.status);
  if (filters.minPrice !== undefined) params.set('min_price', String(filters.minPrice));
  if (filters.maxPrice !== undefined) params.set('max_price', String(filters.maxPrice));
  if (filters.listedWithin) params.set('listed_within', filters.listedWithin);
  if (filters.city) params.set('city', filters.city);
  if (filters.district) params.set('district', filters.district);
  if (filters.neighbourhoodId !== undefined)
    params.set('neighbourhood_id', String(filters.neighbourhoodId));
  if (filters.producerId) params.set('producer_id', filters.producerId);
  if (filters.query) params.set('q', filters.query);
  if (offset) params.set('offset', String(offset));
  if (limit !== undefined) params.set('limit', String(limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export interface ListingsPage {
  items: Listing[];
  /** Total matches, not the page length — the caller shows what is still hidden. */
  total: number;
}

interface ApiPublicListingsPage {
  items: ApiPublicListingCard[];
  total: number;
}

/**
 * One page of the approved-listing feed. The server pages (LIMIT 500 with no
 * spatial filter used to silently drop the oldest listings past that mark);
 * callers accumulate pages themselves, same pattern as getSearchSuggestions.
 */
export async function getListings(
  filters: ListingFilters = {},
  offset = 0,
  limit?: number
): Promise<ListingsPage> {
  const page = await api<ApiPublicListingsPage>(
    `/listings/public${publicListingsQuery(filters, offset, limit)}`
  );
  return { items: page.items.map(toPublicListingCard), total: page.total };
}

/** Nearest active listings regardless of filters — empty-result fallback. */
export async function getNearbyListings(limit = 4): Promise<Listing[]> {
  // FIXTURE-GAP: no real distance sort yet (needs viewer geolocation) — newest first instead.
  const { items } = await getListings({}, 0, limit);
  return items;
}

export interface FavoriteIds {
  listingIds: string[];
  producerIds: string[];
}

interface ApiFavoriteIds {
  listingIds: number[];
  producerIds: number[];
}

/**
 * Lightweight — call once (if authed) on any screen that needs to mark hearts
 * correctly (Explore's cards, listing detail, producer profile) without
 * making the public feed/detail endpoints auth-aware.
 */
export async function getFavoriteIds(): Promise<FavoriteIds> {
  const { listingIds, producerIds } = await api<ApiFavoriteIds>('/favorites/ids');
  return { listingIds: listingIds.map(String), producerIds: producerIds.map(String) };
}

/** Returns the new state (true = now favorited). */
export async function toggleFavoriteListing(listingId: string): Promise<boolean> {
  const { favorited } = await api<{ favorited: boolean }>(`/favorites/listings/${listingId}`, {
    method: 'POST',
  });
  return favorited;
}

/** Full card data, same shape as getListings — approved+active+unexpired only. */
/**
 * A saved listing. One that is not public right now arrives as a stub — the
 * server sends nothing but its id and title — so the list can say it is gone.
 */
export type FavoriteListing =
  (Listing & { available: true }) | { available: false; id: string; title: string };

type ApiFavoriteListing =
  (ApiPublicListingCard & { available: true }) | { available: false; id: number; title: string };

export const toFavoriteListing = (row: ApiFavoriteListing): FavoriteListing =>
  row.available
    ? { ...toPublicListingCard(row), available: true }
    : { available: false, id: String(row.id), title: row.title };

export async function getFavoriteListings(): Promise<FavoriteListing[]> {
  const rows = await api<ApiFavoriteListing[]>('/favorites/listings');
  return rows.map(toFavoriteListing);
}

/** A single listing's marker. Shaped for useListingIcons, which draws the emoji. */
export interface MapPoint {
  type: 'point';
  id: string;
  lat: number;
  lng: number;
  categoryId: string;
  productIcon: string;
  productIconKey: string;
}

/** Several listings that would overlap at this zoom, drawn as one disc. */
export interface MapCluster {
  type: 'cluster';
  count: number;
  lat: number;
  lng: number;
}

export type MapItem = MapPoint | MapCluster;

export interface MapFeatures {
  items: MapItem[];
  /** Every listing in view, clustered or not. */
  total: number;
}

interface ApiMapItem {
  type: 'point' | 'cluster';
  id?: number;
  count?: number;
  lat: number;
  lng: number;
  category_id?: number;
  product_icon?: string | null;
  product_icon_key?: string | null;
}

interface ApiMapFeatures {
  items: ApiMapItem[];
  total: number;
}

/**
 * What the map draws inside the current viewport.
 *
 * Separate from getListings because the list and the map want different things
 * — the list wants cards, the map wants coordinates, and the map needs every
 * listing in the current viewport at once rather than a page of the newest N.
 *
 * `zoom` is the integer map zoom. The cluster grid is anchored to the Web
 * Mercator tile pyramid at that level, which is what stops clusters reshuffling
 * when the user merely pans.
 */
export async function getMapFeatures(
  filters: ListingFilters,
  bbox: [number, number, number, number],
  zoom: number
): Promise<MapFeatures> {
  const query = publicListingsQuery(filters, 0);
  const sep = query ? '&' : '?';
  const res = await api<ApiMapFeatures>(
    `/listings/map${query}${sep}bbox=${bbox.join(',')}&zoom=${Math.floor(zoom)}`
  );

  return {
    total: res.total,
    items: res.items.map((i) =>
      i.type === 'cluster'
        ? { type: 'cluster', count: i.count ?? 0, lat: i.lat, lng: i.lng }
        : {
            type: 'point',
            id: String(i.id),
            lat: i.lat,
            lng: i.lng,
            categoryId: String(i.category_id),
            productIcon: i.product_icon ?? '',
            productIconKey: i.product_icon_key ?? '',
          }
    ),
  };
}

/** Where the matching listings are, before the map knows where to look. */
export interface ListingExtent {
  /** null when nothing matches — the caller leaves the camera where it is. */
  bbox: Bbox | null;
  total: number;
}

/**
 * The bounding box of every match, viewport ignored.
 *
 * Separate from getMapFeatures because it answers a different question: that
 * one draws what is in view, this one says where to look. Picking a subcategory
 * while zoomed into Istanbul has to reach listings in Trabzon, which a
 * viewport-bounded request can never report.
 */
export async function getListingExtent(filters: ListingFilters): Promise<ListingExtent> {
  return api<ListingExtent>(`/listings/map/extent${publicListingsQuery(filters, 0)}`);
}
